import { useTranslation } from 'react-i18next'
import type { Column } from '@tanstack/react-table'
import { Button } from '@/shared/components/ui/button'
import { cn } from '@/shared/lib/utils'
import type { FilterValue } from '@/shared/lib/api/api-types'

interface BooleanFilterProps<TData, TValue> {
  column: Column<TData, TValue>
  onClose: () => void
  labels?: { true: string; false: string }
}

export function BooleanFilter<TData, TValue>({
  column,
  onClose,
  labels,
}: BooleanFilterProps<TData, TValue>) {
  const { t } = useTranslation()
  const optionLabels = labels ?? { true: t('dataTable.active'), false: t('dataTable.inactive') }
  const current = column.getFilterValue() as Extract<FilterValue, { filterType: 'set' }> | undefined
  const selected = current?.values?.[0]

  function select(value: string) {
    if (selected === value) {
      column.setFilterValue(undefined)
    } else {
      column.setFilterValue({ filterType: 'set', values: [value] })
    }
    onClose()
  }

  return (
    <div className="flex flex-col gap-1 p-2 w-36">
      <Button
        variant="ghost"
        size="sm"
        className={cn('h-7 justify-start text-xs', selected === 'true' && 'bg-muted font-semibold')}
        onClick={() => select('true')}
      >
        {optionLabels.true}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className={cn('h-7 justify-start text-xs', selected === 'false' && 'bg-muted font-semibold')}
        onClick={() => select('false')}
      >
        {optionLabels.false}
      </Button>
      {selected && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 justify-start text-xs text-muted-foreground"
          onClick={() => { column.setFilterValue(undefined); onClose() }}
        >
          {t('dataTable.clear')}
        </Button>
      )}
    </div>
  )
}
